import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, ShieldAlert, Download, RefreshCw, Loader2, Search,
  AlertTriangle, Users, FileText, Filter,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Activity, label: 'Activity Ledger', active: true },
  { icon: ShieldAlert, label: 'Ops', href: '/admin/ops' },
  { icon: Users, label: 'Users', href: '/admin/users' },
];

const SEVERITY_COLOR = {
  high: 'border-red-300 bg-red-50 text-red-800',
  medium: 'border-amber-300 bg-amber-50 text-amber-800',
  low: 'border-blue-300 bg-blue-50 text-blue-800',
};

export default function AdminActivityLedger() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [signals, setSignals] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    actor_id: '',
    since_days: 7,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsResp, summaryResp, signalsResp, actionsResp] = await Promise.all([
        axios.get(`${API}/admin/activity`, {
          ...getAuthHeader(),
          params: {
            action: filters.action || undefined,
            resource_type: filters.resource_type || undefined,
            actor_id: filters.actor_id || undefined,
            since_days: filters.since_days,
            limit: 200,
          },
        }),
        axios.get(`${API}/admin/activity/summary?since_days=30`, getAuthHeader()),
        axios.get(`${API}/admin/activity/breach-signals`, getAuthHeader()),
        axios.get(`${API}/admin/activity/actions`, getAuthHeader()),
      ]);
      setRows(rowsResp.data.rows || []);
      setSummary(summaryResp.data);
      setSignals(signalsResp.data.signals || []);
      setActions(actionsResp.data.actions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load activity ledger');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, filters]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const resp = await axios.get(`${API}/admin/activity/export.csv`, {
        ...getAuthHeader(),
        params: {
          since_days: filters.since_days,
          action: filters.action || undefined,
          resource_type: filters.resource_type || undefined,
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `caraloom-admin-activity-${now}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV export downloaded (audit-logged)');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Admin Activity Ledger"
      subtitle="Every audit-logged action across the platform, with breach-signal heuristics. HIPAA §164.308(a)(1)(ii)(D) regular audit review."
      testId="admin-activity-ledger-page"
    >
      {loading ? (
        <div className="cn-card p-10 text-center" data-testid="ledger-loading">
          <Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Breach-signal banner */}
          {signals.length > 0 && (
            <div
              className="cn-card p-5 border border-red-200 bg-red-50/50"
              data-testid="ledger-breach-signals"
            >
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="cn-display text-base text-cn-text">
                  {signals.length} breach signal{signals.length === 1 ? '' : 's'} in the last 24h
                </h3>
                <a
                  href="/app/memory/BREACH_RESPONSE_RUNBOOK.md"
                  className="text-xs text-cn-primary ml-auto"
                  data-testid="ledger-runbook-link"
                >
                  Response runbook →
                </a>
              </div>
              <div className="space-y-2">
                {signals.map((s, i) => (
                  <div
                    key={`${s.signal}-${i}`}
                    className={`border-l-4 pl-3 py-2 rounded text-xs ${SEVERITY_COLOR[s.severity] || SEVERITY_COLOR.low}`}
                    data-testid={`ledger-signal-${s.signal}`}
                  >
                    <div className="font-semibold uppercase tracking-wide text-[10px]">
                      {s.severity} · {s.signal.replace(/_/g, ' ')}
                    </div>
                    <div>{s.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {signals.length === 0 && (
            <div
              className="cn-card p-4 border border-emerald-200 bg-emerald-50/40 text-emerald-800 text-sm flex items-center gap-2"
              data-testid="ledger-no-signals"
            >
              <ShieldAlert className="w-4 h-4" />
              No unusual activity signals in the last 24h.
            </div>
          )}

          {/* Summary tiles */}
          {summary && (
            <div className="grid sm:grid-cols-4 gap-3" data-testid="ledger-summary">
              <SummaryTile label="Total (30d)" value={summary.total?.toLocaleString() || 0} />
              <SummaryTile label="Distinct actions" value={summary.by_action?.length || 0} />
              <SummaryTile label="Distinct roles" value={summary.by_role?.length || 0} />
              <SummaryTile label="Distinct resources" value={summary.by_resource?.length || 0} />
            </div>
          )}

          {/* Filter + export bar */}
          <div className="cn-card p-4 flex flex-wrap items-center gap-3" data-testid="ledger-filters">
            <Filter className="w-4 h-4 text-cn-text-muted" />
            <select
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              className="cn-input !py-1.5 !px-2 text-xs"
              data-testid="ledger-filter-action"
            >
              <option value="">any action</option>
              {actions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <Input
              placeholder="resource_type"
              value={filters.resource_type}
              onChange={(e) => setFilters((f) => ({ ...f, resource_type: e.target.value }))}
              className="!py-1.5 max-w-[160px] text-xs"
              data-testid="ledger-filter-resource"
            />
            <Input
              placeholder="actor_id"
              value={filters.actor_id}
              onChange={(e) => setFilters((f) => ({ ...f, actor_id: e.target.value }))}
              className="!py-1.5 max-w-[220px] text-xs"
              data-testid="ledger-filter-actor"
            />
            <select
              value={filters.since_days}
              onChange={(e) => setFilters((f) => ({ ...f, since_days: parseInt(e.target.value, 10) }))}
              className="cn-input !py-1.5 !px-2 text-xs"
              data-testid="ledger-filter-days"
            >
              <option value={1}>last 24h</option>
              <option value={7}>last 7d</option>
              <option value={30}>last 30d</option>
              <option value={90}>last 90d</option>
            </select>
            <Button onClick={load} className="cn-btn cn-btn-ghost !py-1.5 !px-3 ml-auto text-xs" data-testid="ledger-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button onClick={exportCsv} className="cn-btn cn-btn-primary !py-1.5 !px-3 text-xs" data-testid="ledger-export-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Button>
          </div>

          {/* Rows table */}
          <div className="cn-card p-0 overflow-hidden" data-testid="ledger-rows">
            <div className="max-h-[560px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-cn-bg/40 sticky top-0 border-b border-cn-border/50">
                  <tr className="text-left text-cn-text-muted">
                    <th className="p-2">When</th>
                    <th className="p-2">Actor</th>
                    <th className="p-2">Role</th>
                    <th className="p-2">Action</th>
                    <th className="p-2">Resource</th>
                    <th className="p-2">Org</th>
                    <th className="p-2">Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-6 text-center text-cn-text-muted">
                        No activity in this window.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr
                      key={r.id || `${r.ts}-${r.action}-${r.actor_id}`}
                      className="border-b border-cn-border/30 hover:bg-cn-bg/20"
                      data-testid={`ledger-row-${(r.id || '').slice(0, 8)}`}
                    >
                      <td className="p-2 text-cn-text-muted whitespace-nowrap">
                        {new Date(r.ts).toLocaleString()}
                      </td>
                      <td className="p-2 font-mono">{(r.actor_id || '—').slice(0, 8)}</td>
                      <td className="p-2">{r.actor_role || '—'}</td>
                      <td className="p-2 font-medium text-cn-text">{r.action}</td>
                      <td className="p-2 text-cn-text-muted">
                        {r.resource_type}
                        {r.resource_id ? <span className="opacity-60"> · {r.resource_id.slice(0, 8)}</span> : null}
                      </td>
                      <td className="p-2 font-mono text-cn-text-muted">{(r.org_id || '').slice(0, 8) || '—'}</td>
                      <td className="p-2 text-cn-text-muted max-w-[280px] truncate">
                        {r.metadata ? JSON.stringify(r.metadata).slice(0, 120) : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="cn-card p-4" data-testid={`ledger-tile-${label.replace(/\W/g, '-').toLowerCase()}`}>
      <div className="text-[11px] uppercase tracking-wider text-cn-text-muted">{label}</div>
      <div className="cn-display text-2xl text-cn-text mt-1">{value}</div>
    </div>
  );
}
